import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { CustomTextTitle } from "./draggableFileds/TextTitle";
import { CustomTextInput } from "./draggableFileds/TextInputFields";
import { CustomNumberInput } from "./draggableFileds/CustomNumberInput";
import { CustomTextarea } from "./draggableFileds/CustomTextarea";
import { CustomCheckbox } from "./draggableFileds/CustomCheckbox";
import { CustomRadio } from "./draggableFileds/CustomRadio";
import { CustomSelect } from "./draggableFileds/CustomSelect";
import { CustomDivider } from "./draggableFileds/CustomDivider";
import { CustomTextBlock } from "./draggableFileds/CustomTextBlock";
import { CustomDatePicker } from "./draggableFileds/CustomDatePicker";
import { CustomTimePicker } from "./draggableFileds/CustomTimePicker";
import { CustomButton } from "./draggableFileds/CustomButton";
import { CustomImage } from "./draggableFileds/CustomImage";
import { CustomMultiSelect } from "./draggableFileds/CustomMultiSelect";
import { CustomFileInput } from "./draggableFileds/CustomFileInput";
import {
  getFormsDetails,
  submitFormResponse,
} from "../../services/Users-services";
import { openNewForm } from "../../services/redux/reducers/customFormBuildeSclice";
import { showErrorMessages, callToast } from "../../services/utilityFunctions";
import { useParams, useNavigate } from "react-router-dom";

const FormPreview = ({
  onClose,
  isModal = false,
  isSubmission = false,
  formId = null,
  navigate,
  onSubmit = submitFormResponse,
}) => {
  const { id } = useParams();

  formId = id;
  const dispatch = useDispatch();
  const formBuilder = useSelector((state) => state.customFormBuilder);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [formData, setFormData] = useState({}); // Simplified: fieldName -> value
  const [submissionResult, setSubmissionResult] = useState(null);
  const [formDetails, setFormDetails] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [errors, setErrors] = useState({});
  const [fieldMapping, setFieldMapping] = useState({}); // instanceId -> fieldName mapping

  console.log("FormPreview props:", { isSubmission, formId, formBuilder });

  // Create field mapping and initialize form data
  const initializeFormData = (formMetadata, fields) => {
    const mapping = {};
    const initialData = {};

    // Create mapping between instanceId and field name
    formMetadata.forEach((section) => {
      if (section.elements) {
        section.elements.forEach((element) => {
          // Skip non-input elements
          if (
            ["divider", "text", "text-title", "button", "image"].includes(
              element.id
            )
          ) {
            return;
          }

          const elementLabel = element.style?.label?.label;
          if (elementLabel) {
            // Find the corresponding field in _FIELDS_
            const field = fields.find((f) => f.title === elementLabel);
            if (field) {
              mapping[element.instanceId] = field.name;

              // Initialize with appropriate default values
              switch (element.id) {
                case "checkbox":
                  initialData[field.name] = false;
                  break;
                case "multiSelect":
                  initialData[field.name] = [];
                  break;
                default:
                  initialData[field.name] = "";
                  break;
              }
            }
          }
        });
      }
    });

    setFieldMapping(mapping);
    setFormData(initialData);
  };

  // Fetch form details when isSubmission is true and formId is provided
  useEffect(() => {
    const fetchFormDetails = async () => {
      if (!isSubmission || !formId) return;

      setLoadingData(true);
      setFetchError(null);

      try {
        console.log("Fetching form details for ID:", formId);
        const response = await getFormsDetails(formId);

        if (response.status === "success") {
          console.log("Form details fetched:", response.data);
          setFormDetails(response.data);

          // Parse and set form metadata
          const formMetadata = getFormMetadata(response.data);
          if (formMetadata && formMetadata.length > 0) {
            console.log("Dispatching openNewForm with metadata:", formMetadata);
            dispatch(openNewForm({ metadata: formMetadata }));

            // Initialize form data with field mapping
            if (response.data._FIELDS_) {
              initializeFormData(formMetadata, response.data._FIELDS_);
            }
          } else {
            console.warn("No valid form metadata found");
            setFetchError("Form metadata is empty or invalid");
          }
        } else {
          console.error("Failed to fetch form details:", response);
          setFetchError("Failed to load form details");
          showErrorMessages({ data: response, navigate });
        }
      } catch (error) {
        console.error("Error fetching form details:", error);
        setFetchError(
          error.message || "An error occurred while loading the form"
        );
      } finally {
        setLoadingData(false);
      }
    };

    fetchFormDetails();
  }, [isSubmission, formId, dispatch, navigate]);

  // Parse form metadata function
  const getFormMetadata = (details) => {
    if (!details || !details["_METADATA_"]) {
      console.log("Missing metadata in form details");
      return [];
    }

    try {
      console.log("Raw metadata:", details["_METADATA_"]);
      const parsed = JSON.parse(details["_METADATA_"]);
      console.log("Parsed metadata:", parsed);

      if (!Array.isArray(parsed)) {
        console.warn("Metadata is not an array, converting to array format");
        return [parsed];
      }

      return parsed;
    } catch (e) {
      console.error("Error parsing form metadata:", e);
      return [];
    }
  };

  // Simplified form data change handler
  const handleFormChange = (instanceId, value) => {
    const fieldName = fieldMapping[instanceId];
    if (fieldName) {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: value,
      }));

      // Clear field error when user starts typing
      if (errors[fieldName]) {
        setErrors((prev) => ({
          ...prev,
          [fieldName]: "",
        }));
      }
    }
  };

  // Get field name for an element
  const getFieldName = (instanceId) => {
    return fieldMapping[instanceId] || "";
  };

  // Get field value for an element
  const getFieldValue = (instanceId) => {
    const fieldName = fieldMapping[instanceId];
    return fieldName ? formData[fieldName] : "";
  };

  // Render a form element based on its type
  const renderFormElement = (element) => {
    const fieldName = getFieldName(element.instanceId);
    const fieldValue = getFieldValue(element.instanceId);

    const commonProps = isSubmission
      ? {
          instanceId: element.instanceId,
          isSubmission: true,
          // Create mock field and form objects for Formik-like behavior
          field: {
            name: fieldName,
            value: fieldValue,
          },
          form: {
            setFieldValue: (name, value) => {
              handleFormChange(element.instanceId, value);
            },
          },
          disabled: false, // Always keep fields enabled when isSubmission is true
        }
      : {
          instanceId: element.instanceId,
        };

    switch (element.id) {
      case "text-title":
        return <CustomTextTitle {...commonProps} />;
      case "text-input":
        return <CustomTextInput {...commonProps} />;
      case "number-input":
        return <CustomNumberInput {...commonProps} />;
      case "textarea":
        return <CustomTextarea {...commonProps} />;
      case "checkbox":
        return <CustomCheckbox {...commonProps} />;
      case "radio":
        return <CustomRadio {...commonProps} />;
      case "select":
        return <CustomSelect {...commonProps} />;
      case "multiSelect":
        return <CustomMultiSelect {...commonProps} />;
      case "file-input":
        return <CustomFileInput {...commonProps} />;
      case "divider":
        return <CustomDivider {...commonProps} />;
      case "text":
        return <CustomTextBlock {...commonProps} />;
      case "date":
        return <CustomDatePicker {...commonProps} />;
      case "time":
        return <CustomTimePicker {...commonProps} />;
      case "button":
        return <CustomButton {...commonProps} />;
      case "image":
        return <CustomImage {...commonProps} />;
      default:
        return <div>Unknown element type: {element.id}</div>;
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    if (!formDetails || !formDetails._FIELDS_) {
      return newErrors;
    }

    // Validate based on field definitions
    formDetails._FIELDS_.forEach((field) => {
      const fieldValue = formData[field.name];

      // Required field validation
      if (field.required === "Y") {
        if (
          !fieldValue ||
          (Array.isArray(fieldValue) && fieldValue.length === 0) ||
          (typeof fieldValue === "string" && fieldValue.trim() === "")
        ) {
          newErrors[field.name] = `${field.title} is required`;
          return;
        }
      }

      // Type-specific validations can be added here based on field.type
      if (fieldValue && field.type === "VARCHAR") {
        // Email validation if it's an email field
        if (field.name.includes("email")) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(fieldValue)) {
            newErrors[field.name] = "Please enter a valid email address";
          }
        }

        // Length validation
        if (fieldValue.length < field.min) {
          newErrors[field.name] = `Minimum length is ${field.min} characters`;
        }
        if (fieldValue.length > field.max) {
          newErrors[field.name] = `Maximum length is ${field.max} characters`;
        }
      }

      // Custom regex validation
      if (fieldValue && field.regex) {
        try {
          const regex = new RegExp(field.regex);
          if (!regex.test(fieldValue)) {
            newErrors[field.name] = `${field.title} format is invalid`;
          }
        } catch (error) {
          console.warn("Invalid regex pattern:", field.regex);
        }
      }
    });

    setErrors(newErrors);
    return newErrors;
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!isSubmission) {
      return;
    }

    // Validate form first
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setSubmissionResult({
        success: false,
        message: "Please fill in all required fields",
        errors: validationErrors,
      });
      return;
    }

    setLoading(true);
    setSubmissionResult(null);
    setErrors({});

    try {
      // Simplified submission data
      const submissionData = {
        formId: formId,
        response: formData, // Direct field name -> value mapping
      };

      console.log("Form data being submitted:", submissionData);

      // Submit form using the provided onSubmit function or default
      const response = await onSubmit(submissionData);

      if (response.status === "success") {
        callToast("success", "Success", "Form submitted successfully!");
        setSubmissionResult({
          success: true,
          message: "Form submitted successfully!",
        });

        // Optional: Reset form after successful submission
        // setFormData({});
      } else {
        console.error("Form submission failed:", response);

        // Handle validation errors from server
        const resError = showErrorMessages({
          data: response,
          navigate,
          formFilds: [], // Add form field mapping if needed
        });

        if (resError != null && resError !== undefined) {
          setErrors(resError);
        }

        setSubmissionResult({
          success: false,
          message:
            response.message || "Failed to submit form. Please try again.",
          error: response.message,
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmissionResult({
        success: false,
        message:
          "An error occurred while submitting the form. Please try again.",
        error: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Show initial loading when fetching form details
  if (isSubmission && loadingData) {
    return (
      <div
        className="form-preview d-flex align-items-center justify-content-center"
        style={{
          minHeight: isModal ? "300px" : "calc(100vh - 7vh)",
          backgroundColor: isModal ? "transparent" : "#f0f2f5",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #0d6efd",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 1rem",
            }}
          ></div>
          <p style={{ color: "#6c757d", margin: 0 }}>Loading form...</p>
        </div>
      </div>
    );
  }

  // Show error if form fetch failed
  if (isSubmission && fetchError) {
    return (
      <div
        className="form-preview d-flex align-items-center justify-content-center"
        style={{
          minHeight: isModal ? "300px" : "calc(100vh - 7vh)",
          backgroundColor: isModal ? "transparent" : "#f0f2f5",
          padding: "2rem",
        }}
      >
        <div
          className="alert alert-danger text-center"
          style={{ maxWidth: "500px" }}
        >
          <h4 className="alert-heading">Error Loading Form</h4>
          <p>{fetchError}</p>
          <hr />
          <button
            className="btn btn-outline-danger"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
          {onClose && (
            <button
              className="btn btn-outline-secondary ms-2"
              onClick={onClose}
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="form-preview"
        style={{
          minHeight: isModal ? "auto" : "calc(100vh - 7vh)",
          backgroundColor: isModal ? "transparent" : "#f0f2f5",
          overflowX: "hidden",
          padding: isModal ? "0" : "2rem 2rem",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          className="container-fluid"
          style={{
            maxWidth: isModal ? "100%" : "800px",
            padding: isModal ? "0" : "",
          }}
        >
          {/* Form Content */}
          <div
            className="preview-content"
            style={{
              maxWidth: "100%",
              margin: "0 auto",
              width: "100%",
            }}
          >
            {formBuilder && formBuilder.length > 0 ? (
              <form onSubmit={handleFormSubmit}>
                <div
                  className="form-container"
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: isModal ? "4px" : "8px",
                    border: "none",
                    padding: isModal ? "1.25rem" : "2rem",
                    boxShadow: isModal
                      ? "none"
                      : "0 4px 12px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                    height: isModal ? "auto" : "calc(100vh - 11vh)",
                    maxHeight: isModal ? "70vh" : "none",
                    overflowY: "auto",
                    opacity: loading ? 0.7 : 1,
                    pointerEvents: loading ? "none" : "auto",
                    position: "relative",
                  }}
                >
                  {/* Loading Overlay */}
                  {loading && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                        borderRadius: isModal ? "4px" : "8px",
                      }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            border: "4px solid #f3f3f3",
                            borderTop: "4px solid #0d6efd",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                            margin: "0 auto 1rem",
                          }}
                        ></div>
                        <p style={{ color: "#6c757d", margin: 0 }}>
                          Submitting form...
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Submission Result Message */}
                  {submissionResult && (
                    <div
                      style={{
                        marginBottom: "1.5rem",
                        padding: "1rem",
                        borderRadius: "8px",
                        backgroundColor: submissionResult.success
                          ? "#d1edda"
                          : "#f8d7da",
                        border: `1px solid ${
                          submissionResult.success ? "#c3e6cb" : "#f5c6cb"
                        }`,
                        color: submissionResult.success ? "#155724" : "#721c24",
                      }}
                    >
                      <strong>
                        {submissionResult.success ? "Success!" : "Error:"}
                      </strong>
                      <p style={{ margin: "0.5rem 0 0", fontSize: "0.9rem" }}>
                        {submissionResult.message}
                      </p>
                      {submissionResult.errors && (
                        <ul
                          style={{
                            margin: "0.5rem 0 0",
                            paddingLeft: "1.5rem",
                          }}
                        >
                          {Object.values(submissionResult.errors).map(
                            (error, index) => (
                              <li key={index} style={{ fontSize: "0.85rem" }}>
                                {error}
                              </li>
                            )
                          )}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* Render all sections as rows */}
                  {formBuilder.map((section, sectionIndex) => (
                    <div
                      key={section.instanceId}
                      className="form-row"
                      style={{
                        marginBottom:
                          sectionIndex < formBuilder.length - 1 ? "1rem" : "0",
                        position: "relative",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {/* Elements in a single row */}
                      <div
                        className="elements-row"
                        style={{
                          display: "flex",
                          flexWrap: "nowrap",
                          gap: "0.75rem",
                          alignItems: "stretch",
                          minHeight: "auto",
                          width: "100%",
                        }}
                      >
                        {section.elements && section.elements.length > 0 ? (
                          section.elements.map((element, elementIndex) => {
                            const fieldName = getFieldName(element.instanceId);

                            return (
                              <div
                                key={element.instanceId}
                                className="preview-element-wrapper"
                                style={{
                                  flex:
                                    element.style?.width === "100%"
                                      ? "1 1 100%"
                                      : "0 0 auto",
                                  minWidth:
                                    element.style?.width === "100%"
                                      ? "0"
                                      : element.style?.width || "auto",
                                  width:
                                    element.style?.width === "100%"
                                      ? "100%"
                                      : element.style?.width || "auto",
                                  animation: isModal
                                    ? ""
                                    : "fadeInUp 0.5s ease-out forwards",
                                  opacity: isModal ? 1 : 0,
                                  animationDelay: isModal
                                    ? ""
                                    : `${0.1 * elementIndex}s`,
                                  padding: "0",
                                  margin: "0",
                                }}
                              >
                                <div
                                  className="element-content"
                                  style={{
                                    backgroundColor: "transparent",
                                    borderRadius: "8px",
                                    padding: "0",
                                    transition: "all 0.2s ease",
                                    height: "100%",
                                  }}
                                >
                                  {renderFormElement(element)}
                                </div>
                                {/* Display field-specific errors */}
                                {fieldName && errors[fieldName] && (
                                  <small className="text-danger d-block mt-1">
                                    {errors[fieldName]}
                                  </small>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div
                            className="empty-row"
                            style={{
                              flex: "1",
                              textAlign: "center",
                              padding: isModal ? "1rem" : "1.5rem",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "8px",
                              border: "2px dashed #dee2e6",
                              color: "#6c757d",
                            }}
                          >
                            Empty row
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Submit Section */}
                  <div
                    className="submit-section"
                    style={{
                      marginTop: isModal ? "1.5rem" : "2rem",
                      paddingTop: isModal ? "1rem" : "1.5rem",
                      borderTop: "2px solid #f0f2f5",
                      textAlign: "center",
                    }}
                  >
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      disabled={loading || loadingData}
                      style={{
                        minWidth: isModal ? "150px" : "180px",
                        padding: isModal ? "0.5rem 1.5rem" : "0.75rem 2rem",
                        fontSize: "1rem",
                        fontWeight: "600",
                        borderRadius: "8px",
                        backgroundColor:
                          loading || loadingData ? "#6c757d" : "#0d6efd",
                        border: "none",
                        boxShadow: isModal
                          ? "0 2px 8px rgba(13, 110, 253, 0.2)"
                          : "0 4px 12px rgba(13, 110, 253, 0.25)",
                        transition: "all 0.3s ease",
                        cursor:
                          loading || loadingData ? "not-allowed" : "pointer",
                      }}
                      onMouseEnter={(e) => {
                        if (!loading && !loadingData) {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 16px rgba(13, 110, 253, 0.35)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading && !loadingData) {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = isModal
                            ? "0 2px 8px rgba(13, 110, 253, 0.2)"
                            : "0 4px 12px rgba(13, 110, 253, 0.25)";
                        }
                      }}
                    >
                      {loading ? (
                        <div className="d-flex align-items-center justify-content-center">
                          <div
                            className="spinner-border spinner-border-sm text-light me-2"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                          Submitting...
                        </div>
                      ) : (
                        "Submit Form"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <div
                className="empty-form"
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: isModal ? "4px" : "16px",
                  padding: isModal ? "2rem 1rem" : "4rem 2rem",
                  textAlign: "center",
                  boxShadow: isModal
                    ? "none"
                    : "0 4px 20px rgba(0, 0, 0, 0.08)",
                  border: "none",
                  maxHeight: isModal ? "60vh" : "auto",
                  overflowY: isModal ? "auto" : "visible",
                }}
              >
                <div
                  className="empty-icon"
                  style={{
                    marginBottom: isModal ? "1rem" : "1.5rem",
                    color: "#cbd5e0",
                  }}
                >
                  <i
                    className="bi bi-layout-text-window-reverse"
                    style={{
                      fontSize: isModal ? "3rem" : "4rem",
                      display: "block",
                      animation: "pulse 2s infinite",
                    }}
                  ></i>
                </div>
                <h3
                  style={{
                    fontSize: isModal ? "1.25rem" : "1.5rem",
                    fontWeight: "600",
                    color: "#2d3748",
                    marginBottom: isModal ? "0.75rem" : "1rem",
                  }}
                >
                  {isSubmission ? "Form not available" : "Your form is empty"}
                </h3>
                <p
                  style={{
                    color: "#718096",
                    fontSize: isModal ? "0.9rem" : "1rem",
                    marginBottom: isModal ? "1.5rem" : "2rem",
                    maxWidth: isModal ? "350px" : "400px",
                    margin: isModal ? "0 auto 1.5rem" : "0 auto 2rem",
                  }}
                >
                  {isSubmission
                    ? "This form is currently not available or has no content."
                    : "Add sections and elements to your form builder to see them in this preview."}
                </p>
                {onClose && (
                  <button
                    onClick={onClose}
                    className="btn btn-primary"
                    style={{
                      padding: isModal ? "0.5rem 1.5rem" : "0.75rem 2rem",
                      fontSize: isModal ? "0.9rem" : "1rem",
                      fontWeight: "600",
                      borderRadius: "8px",
                      backgroundColor: "#0d6efd",
                      border: "none",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(13, 110, 253, 0.25)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    {isSubmission ? "Close" : "Back to Design Mode"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Animation Styles */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0% {
            opacity: 0.6;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0.6;
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .form-preview::-webkit-scrollbar {
          width: 8px;
        }

        .form-preview::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .form-preview::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 4px;
        }

        .form-preview::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }

        .btn {
          transition: all 0.3s ease;
        }

        .btn:hover:not(:disabled) {
          transform: translateY(-2px);
        }

        .container-fluid {
          height: 100%;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .elements-row {
            flex-wrap: wrap !important;
          }

          .preview-element-wrapper {
            flex: 1 1 100% !important;
            min-width: 0 !important;
          }

          .form-container {
            padding: 1.5rem !important;
          }

          .form-preview {
            padding: 1.5rem 0.5rem !important;
          }

          .container-fluid {
            width: 90%;
          }
        }

        @media (max-width: 576px) {
          .form-container {
            padding: 1rem !important;
          }

          .container-fluid {
            width: 95%;
          }
        }
      `}</style>
    </>
  );
};

export default FormPreview;
